import {
  COMMON_FACILITY_ICON_KEYS,
  COMMON_FACILITY_PRIORITY_SET,
  FACILITY_CATEGORIES,
  FACILITY_CATEGORY_ORDER,
  MAX_COMMON_FACILITIES,
  facilityTaxonomyByNormalizedLabel,
} from "@/features/hotels/utils/facilities/catalog";
import { parseHotelFacilities } from "@/features/hotels/utils/facilities/normalization";
import type {
  CommonFacilityItem,
  FacilityGroup,
  FacilityGroupItem,
  HotelFacilitiesDisplayModel,
  NonExtraFacilityCategoryKey,
} from "@/features/hotels/utils/facilities/types";

type MatchedFacility = {
  taxonomyKey: string;
  taxonomyLabel: string;
  categoryKey: NonExtraFacilityCategoryKey;
  known: boolean;
  rank?: number;
  value?: number;
};

type RankedCommonCandidate = MatchedFacility & {
  rank: number;
};

const isRankedCommonCandidate = (
  item: MatchedFacility,
): item is RankedCommonCandidate => item.rank !== undefined;

const isDefinedRankedCandidate = (
  item: RankedCommonCandidate | undefined,
): item is RankedCommonCandidate => item !== undefined;

const matchFacilities = (
  facilities: string | null | undefined,
): {
  matched: MatchedFacility[];
  unmatched: FacilityGroupItem[];
} => {
  const parsedFacilities = parseHotelFacilities(facilities);
  const matched: MatchedFacility[] = [];
  const unmatched: FacilityGroupItem[] = [];

  parsedFacilities.forEach((parsed) => {
    const taxonomyItem = facilityTaxonomyByNormalizedLabel.get(
      parsed.normalizedLabel,
    );

    if (!taxonomyItem) {
      if (parsed.categoryHint) {
        matched.push({
          taxonomyKey: `${parsed.categoryHint}-${parsed.normalizedLabel}`,
          taxonomyLabel: parsed.originalLabel,
          categoryKey: parsed.categoryHint,
          known: false,
          value: parsed.value,
        });
        return;
      }

      unmatched.push({
        key: `extra-${parsed.normalizedLabel}`,
        label:
          parsed.value !== undefined
            ? `${parsed.originalLabel}: ${parsed.value}`
            : parsed.originalLabel,
        value: parsed.value,
      });
      return;
    }

    if (taxonomyItem.categoryKey === "extra") {
      unmatched.push({
        key: taxonomyItem.key,
        label: taxonomyItem.label,
        localizationKey:
          taxonomyItem.withValue && parsed.value !== undefined
            ? taxonomyItem.key
            : undefined,
        value: taxonomyItem.withValue ? parsed.value : undefined,
      });
      return;
    }

    matched.push({
      taxonomyKey: taxonomyItem.key,
      taxonomyLabel: taxonomyItem.label,
      categoryKey: taxonomyItem.categoryKey,
      known: true,
      rank: taxonomyItem.rank,
      value: taxonomyItem.withValue ? parsed.value : undefined,
    });
  });

  return { matched, unmatched };
};

const selectCommonFacilities = (
  matched: MatchedFacility[],
): CommonFacilityItem[] => {
  const rankedCommonCandidates = matched
    .filter(isRankedCommonCandidate)
    .filter((item) => COMMON_FACILITY_ICON_KEYS.has(item.taxonomyKey))
    .sort((a, b) => a.rank - b.rank);

  const candidatesByKey = new Map(
    rankedCommonCandidates.map((item) => [item.taxonomyKey, item]),
  );

  const prioritizedCandidates = [...COMMON_FACILITY_PRIORITY_SET]
    .map((key) => candidatesByKey.get(key))
    .filter(isDefinedRankedCandidate);

  const remainingCandidates = rankedCommonCandidates.filter(
    (item) => !COMMON_FACILITY_PRIORITY_SET.has(item.taxonomyKey),
  );

  return [...prioritizedCandidates, ...remainingCandidates]
    .slice(0, MAX_COMMON_FACILITIES)
    .map((item) => ({
      key: item.taxonomyKey,
      label: item.taxonomyLabel,
      rank: item.rank,
      localizationKey: item.taxonomyKey,
    }));
};

const buildGroups = (
  matched: MatchedFacility[],
  unmatched: FacilityGroupItem[],
): FacilityGroup[] => {
  const groupedItems = new Map<
    NonExtraFacilityCategoryKey,
    FacilityGroupItem[]
  >();

  matched.forEach((item) => {
    const currentItems = groupedItems.get(item.categoryKey) ?? [];
    currentItems.push({
      key: item.taxonomyKey,
      label: item.taxonomyLabel,
      localizationKey: item.known ? item.taxonomyKey : undefined,
      value: item.value,
    });
    groupedItems.set(item.categoryKey, currentItems);
  });

  groupedItems.forEach((items) => {
    items.sort((a, b) => a.label.localeCompare(b.label));
  });

  const groups: FacilityGroup[] = [];

  FACILITY_CATEGORY_ORDER.forEach((categoryKey) => {
    if (categoryKey === "extra") {
      return;
    }

    const items = groupedItems.get(categoryKey);
    if (!items || items.length === 0) {
      return;
    }

    const category = FACILITY_CATEGORIES[categoryKey];
    groups.push({
      key: categoryKey,
      label: category.label,
      localizationKey: categoryKey,
      items,
    });
  });

  if (unmatched.length > 0) {
    unmatched.sort((a, b) => a.label.localeCompare(b.label));
    const extraCategory = FACILITY_CATEGORIES.extra;
    groups.push({
      key: extraCategory.key,
      label: extraCategory.label,
      localizationKey: extraCategory.key,
      items: unmatched,
    });
  }

  return groups;
};

export const buildHotelFacilitiesDisplayModel = (
  facilities: string | null | undefined,
): HotelFacilitiesDisplayModel => {
  const { matched, unmatched } = matchFacilities(facilities);

  return {
    commonFacilities: selectCommonFacilities(matched),
    groups: buildGroups(matched, unmatched),
  };
};
